import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import ErrorBanner from "../ui/ErrorBanner";
import Spinner from "../ui/Spinner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (error) {
      setError(
        error.message || "Failed to send reset link. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Success state — email has been sent
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-elevated px-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-center mb-8">
            <a href="/" className="text-3xl font-bold text-text-primary">
              Traces
            </a>
          </div>

          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Check your email
          </h2>
          <p className="text-sm text-text-secondary mb-2">
            If an account exists for{" "}
            <span className="font-medium text-text-primary">{email}</span>,
            we've sent a password reset link. Check your inbox and click the
            link to reset your password.
          </p>
          <p className="text-xs text-text-muted mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>

          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setEmail("");
            }}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>

          <div className="text-center text-sm text-text-secondary mt-6">
            <a href="/login" className="text-text-primary hover:underline">
              Back to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Form state — enter email to request reset
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-elevated px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="text-3xl font-bold text-text-primary">
            Traces
          </a>
          <p className="text-sm text-text-muted mt-1">Reset your password</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <ErrorBanner
              message={error}
              onDismiss={() => setError(null)}
            />
          )}

          <p className="text-sm text-text-secondary">
            Enter the email address associated with your account and we'll send
            you a link to reset your password.
          </p>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Email
            </label>
            <input
              type="email"
              className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-text-primary focus:border-text-primary disabled:opacity-50"
              placeholder="johndoe@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-text-inverse py-2 px-4 rounded-md text-sm hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Spinner size={16} />}
            {submitting ? "Sending..." : "Send Reset Link"}
          </button>
        </form>

        <div className="text-center text-sm text-text-secondary mt-6">
          <a href="/login" className="text-text-primary hover:underline">
            Back to login
          </a>
        </div>
      </div>
    </div>
  );
}
